"use client";

import { useTranslations } from "next-intl";
import {
    SectionShell,
    EditorialTitle,
    GradientText,
    AnimatedBar,
    RevealOnScroll,
} from "@/components/ui/Socials/JoinInstructors/_primitives";

export default function TransparencySection() {
    const t = useTranslations();

    const splits = [
        {
            label: t("joinMentor_transp_split1_label"),
            title: t("joinMentor_transp_split1_title"),
            desc: t("joinMentor_transp_split1_desc"),
            pct: 85,
            tone: "highlight",
        },
        {
            label: t("joinMentor_transp_split2_label"),
            title: t("joinMentor_transp_split2_title"),
            desc: t("joinMentor_transp_split2_desc"),
            pct: 11,
            tone: "neutral",
        },
        {
            label: t("joinMentor_transp_split3_label"),
            title: t("joinMentor_transp_split3_title"),
            desc: t("joinMentor_transp_split3_desc"),
            pct: 4,
            tone: "neutral",
        },
    ];

    return (
        <SectionShell label={t("joinMentor_transp_marker")}>
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start mb-12">
                <RevealOnScroll>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                        {t("joinMentor_transp_eyebrow")}
                    </p>
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl">
                        {t("joinMentor_transp_title_part1")}
                        <br />
                        <GradientText>
                            {t("joinMentor_transp_title_emphasis")}
                        </GradientText>{" "}
                        {t("joinMentor_transp_title_part2")}.
                    </EditorialTitle>
                </RevealOnScroll>
                <RevealOnScroll delay={0.1}>
                    <p
                        className="text-gray-500 dark:text-white/55 italic text-lg lg:text-right"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinMentor_transp_subtitle")}
                    </p>
                </RevealOnScroll>
            </div>

            <div className="space-y-5">
                {splits.map((s, i) => (
                    <RevealOnScroll key={i} delay={i * 0.1}>
                        <div
                            className={`relative rounded-2xl p-6 sm:p-7 ${
                                s.tone === "highlight"
                                    ? "border border-primary/40 bg-gradient-to-br from-primary/15 via-transparent to-transparent"
                                    : "border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03]"
                            }`}
                        >
                            <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-2 font-mono">
                                        {s.label}
                                    </div>
                                    <h3
                                        className={`text-xl sm:text-2xl md:text-3xl font-bold ${
                                            s.tone === "highlight"
                                                ? "text-primary"
                                                : "text-gray-900 dark:text-white"
                                        }`}
                                        style={{
                                            fontFamily:
                                                "'Playfair Display', Georgia, serif",
                                        }}
                                    >
                                        {s.title}
                                    </h3>
                                </div>
                                <div
                                    className={`text-5xl sm:text-6xl md:text-7xl font-bold leading-none ${
                                        s.tone === "highlight"
                                            ? "text-primary"
                                            : "text-gray-600 dark:text-white/55"
                                    }`}
                                    style={{
                                        fontFamily:
                                            "'Playfair Display', Georgia, serif",
                                    }}
                                >
                                    {s.pct}
                                    <span className="text-2xl sm:text-3xl ml-1">%</span>
                                </div>
                            </div>
                            <p className="text-gray-500 dark:text-white/55 text-sm leading-relaxed mb-4 max-w-2xl">
                                {s.desc}
                            </p>
                            <AnimatedBar
                                percent={s.pct}
                                from={
                                    s.tone === "highlight"
                                        ? "from-[#FF7A1A]"
                                        : "from-gray-300 dark:from-white/30"
                                }
                                to={
                                    s.tone === "highlight"
                                        ? "to-[#E51578]"
                                        : "to-gray-300 dark:to-white/30"
                                }
                                delay={0.3 + i * 0.15}
                            />
                        </div>
                    </RevealOnScroll>
                ))}
            </div>

            <RevealOnScroll delay={0.4}>
                <div className="mt-10 p-6 rounded-2xl border border-primary/40">
                    <p
                        className="italic text-gray-700 dark:text-white/80 leading-relaxed"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinMentor_transp_callout_part1")}{" "}
                        <span className="text-primary font-semibold not-italic">
                            {t("joinMentor_transp_callout_emphasis")}
                        </span>{" "}
                        {t("joinMentor_transp_callout_part2")}
                    </p>
                </div>
            </RevealOnScroll>
        </SectionShell>
    );
}
